import { TreatmentRequest } from '@/models/diagnosis';
import { TreatmentRequestDoctor } from '@/models/diagnosis';
import { EDoctorFilterType } from '@/models/diagnosis/doctor-filter';
import { EDoctorSortType, NonF2fDoctor } from '@/models/doctor/telemedicine';
import { CustomPageProps } from '@/types/app';
import { DoctorDetail } from '@/types/doctor';
import { useMinimumLoadingDuration } from '../hooks/useMinimumLoadingDuration';
import DoctorDetailBottomSheet from './DoctorDetailBottomSheet';
import { DOCTOR_SORT_KEY } from './SearchDoctorsSortFilterBottomSheet';
import SearchDoctorsSortFilterBottomSheet from './SearchDoctorsSortFilterBottomSheet';
import TelemedicineDoctorFilter from './TelemedicineDoctorFilter';
import TelemedicineDoctorList from './TelemedicineDoctorList';
import TelemedicineDoctorListLoading from './TelemedicineDoctorListLoading';

const MIN_LOADING_DURATION_MS = 3000;

interface Props extends CustomPageProps {
  searchDoctors?: NonF2fDoctor[];
  isLoadingSearchDoctors: boolean;
  hasNextPageSearchDoctors: boolean;
  treatmentRequest: TreatmentRequest;
  categoryName: string;
  selectedFilters?: EDoctorFilterType[];
  isSearchDoctorSortFilterBottomSheetOpen: boolean;
  selectedSortKey: EDoctorSortType;
  handleSearchDoctorSpecialistOnlySelect: (specialistOnly: boolean) => void;
  handleSearchDoctorSortFilterSelect: (sortKey: DOCTOR_SORT_KEY) => void;
  fetchNextPageSearchDoctors: () => void;
  openSearchDoctorSortFilterBottomSheet: () => void;
  closeSearchDoctorSortFilterBottomSheet: () => void;
  onOpenDoctorDetailBottomSheet: (doctorId: string) => void;
  getDoctorDetailOpenCount: () => number;
  isDoctorDetailBottomSheetOpen: boolean;
  onCloseDoctorDetailBottomSheet: () => void;
  handleSelectDoctor: (doctor?: TreatmentRequestDoctor, doctorPublicId?: string, doctorDetail?: DoctorDetail) => void;
  isEnabledScheduleAlertOpen: boolean;
  selectedStartDisplayTimeSlot: string;
  selectedDoctorDetail: DoctorDetail | null;
  onCloseIsEnabledScheduleAlert: () => void;
}

const TelemedicineDoctorSelect = ({
  client,
  searchDoctors,
  isLoadingSearchDoctors,
  hasNextPageSearchDoctors,
  treatmentRequest,
  categoryName,
  selectedFilters,
  isSearchDoctorSortFilterBottomSheetOpen,
  selectedSortKey,
  handleSearchDoctorSpecialistOnlySelect,
  handleSearchDoctorSortFilterSelect,
  fetchNextPageSearchDoctors,
  openSearchDoctorSortFilterBottomSheet,
  closeSearchDoctorSortFilterBottomSheet,
  onOpenDoctorDetailBottomSheet,
  getDoctorDetailOpenCount,
  isDoctorDetailBottomSheetOpen,
  onCloseDoctorDetailBottomSheet,
  handleSelectDoctor,
  isEnabledScheduleAlertOpen,
  selectedStartDisplayTimeSlot,
  selectedDoctorDetail,
  onCloseIsEnabledScheduleAlert,
}: Props) => {
  const shouldShowLoading = useMinimumLoadingDuration({
    isLoading: isLoadingSearchDoctors,
    minimumDurationMs: MIN_LOADING_DURATION_MS,
  });

  return (
    <>
      {shouldShowLoading || isLoadingSearchDoctors ? (
        <TelemedicineDoctorListLoading categoryName={categoryName} selectedFilters={selectedFilters} />
      ) : (
        <div className='min-h-[calc(100vh-160px)] '>
          <div className='flex flex-col gap-[8px] px-[24px] pt-[12px] pb-[12px]'>
            <p className='typography-ui-24sb text-gray-900'>원하는 의사를 선택해주세요</p>
          </div>
          <TelemedicineDoctorFilter
            treatmentRequest={treatmentRequest}
            openSearchDoctorSortFilterBottomSheet={openSearchDoctorSortFilterBottomSheet}
            handleSearchDoctorSpecialistOnlySelect={handleSearchDoctorSpecialistOnlySelect}
          />
          <div className='h-px bg-gray-40' />
          <TelemedicineDoctorList
            searchDoctors={searchDoctors}
            fetchNextPageSearchDoctors={fetchNextPageSearchDoctors}
            hasNextPageSearchDoctors={hasNextPageSearchDoctors}
            onOpenDoctorDetailBottomSheet={onOpenDoctorDetailBottomSheet}
            getDoctorDetailOpenCount={getDoctorDetailOpenCount}
            treatmentRequest={treatmentRequest}
            selectedFilters={selectedFilters}
          />
        </div>
      )}

      <SearchDoctorsSortFilterBottomSheet
        open={isSearchDoctorSortFilterBottomSheetOpen}
        onClickClose={closeSearchDoctorSortFilterBottomSheet}
        selectedSortKey={selectedSortKey}
        handleSearchDoctorSortFilterSelect={handleSearchDoctorSortFilterSelect}
      />

      <DoctorDetailBottomSheet
        client={client}
        isOpen={isDoctorDetailBottomSheetOpen}
        onClose={onCloseDoctorDetailBottomSheet}
        handleSelectDoctor={handleSelectDoctor}
        isEnabledScheduleAlertOpen={isEnabledScheduleAlertOpen}
        selectedStartDisplayTimeSlot={selectedStartDisplayTimeSlot}
        selectedDoctorDetail={selectedDoctorDetail}
        onCloseIsEnabledScheduleAlert={onCloseIsEnabledScheduleAlert}
      />
    </>
  );
};

export default TelemedicineDoctorSelect;
